/**
 * 2026-08-24 — one CSV writer for the whole portal.
 *
 * Replaces the copy-pasted export blocks in the statistics and archive
 * features, both of which had the same two defects:
 *
 *  - cells were pre-quoted at the call site (`"${broadcast.title}"`) and then
 *    quoted AGAIN row-wide, so every text field landed in Excel wrapped in
 *    literal quotes (`"LIVE/2026/…"`);
 *  - no BOM, so Excel read the UTF-8 bytes as cp1252 and turned every umlaut
 *    into mojibake ("Kitzbühel"). The same trap cost us a round of debugging
 *    in scripts/export_voxtral_broadcasts.sh.
 *
 * Pass raw values — quoting and escaping happen here, exactly once.
 */
import { downloadBlob } from './file-download';

export type CsvCell = string | number | boolean | null | undefined;

/** Quotes every cell once, doubling embedded quotes (RFC 4180). */
export function toCsv(headers: readonly string[], rows: readonly CsvCell[][]): string {
  const cell = (value: CsvCell) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  return [headers as readonly CsvCell[], ...rows]
    .map(row => row.map(cell).join(','))
    .join('\n');
}

/** Builds the CSV and triggers a browser download. */
export function downloadCsv(filename: string, headers: readonly string[], rows: readonly CsvCell[][]): void {
  // \uFEFF (BOM): without it Excel does not detect UTF-8 and mangles umlauts.
  const blob = new Blob(['\uFEFF' + toCsv(headers, rows)], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(filename, blob);
}
